import { useCallback, useEffect, useState } from 'react'
import { SendChangesToEysParams } from '../../api/generated'
import {
  ScaniaAdobeTrackingClickEysEvent,
  pushAdobeEvent,
  ScaniaAdobeEventId,
} from '../../utils/adobeAnalytics'
import { useClient } from '../../utils/useClient'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getSessionIdState } from '../../store/sessionDataSlice'
import { getMarketLanguageState } from '../../store/appSlice'
import { closeAllModals } from '../../store/modalSlice'
import { getTruckInfoState } from '../../store/truckInfoSlice'
import { TdsButton, TdsModal } from '@scania/tegel-react'

function sendTrackingEvent({ success }: { success: boolean }) {
  if (!success) {
    // The new tracking event has no success property, which means we should
    // probably not sent this event for failures.
    return
  }
  const trackingEvent: ScaniaAdobeTrackingClickEysEvent = {
    event: ScaniaAdobeEventId.EysClick,
  }
  pushAdobeEvent(trackingEvent)
}

interface ModalSendChangesToEysProps {
  handleSuccess: () => void
}

enum SendStatus {
  WAITING,
  SENDING,
  SUCCESS,
  FAILURE,
}

export function ModalSendChangesToEys({
  handleSuccess,
}: ModalSendChangesToEysProps): JSX.Element {
  const t2 = useTextsV2()
  const client = useClient()
  const dispatch = useAppDispatch()
  const sessionId = useAppSelector(getSessionIdState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const publicConfigId = useAppSelector(getTruckInfoState)?.publicConfigId
  const [status, setStatus] = useState<SendStatus>(SendStatus.WAITING)
  const [title, setTitle] = useState<string>('')
  const [dialogText, setDialogText] = useState<string>('')

  const handleSendChanges = () => {
    const asyncWrapper = async () => {
      if (!client) {
        return
      }
      if (!marketLanguage) {
        return
      }
      const p: SendChangesToEysParams = {
        sessionId,
        etelLanguage: marketLanguage.language.toString(),
        parentPublicConfigId: publicConfigId || undefined,
      }
      setStatus(SendStatus.SENDING)
      try {
        await client.sendChangesToEys(p)
        setStatus(SendStatus.SUCCESS)
        sendTrackingEvent({ success: true })
        handleSuccess()
      } catch {
        setStatus(SendStatus.FAILURE)
        sendTrackingEvent({ success: false })
      }
    }
    asyncWrapper()
  }

  useEffect(() => {
    switch (status) {
      case SendStatus.WAITING:
        setTitle(t2(TextId.EYS_SEND_CHANGES_SALES_HEADER))
        setDialogText(t2(TextId.EYS_SEND_CHANGES_TEXT))
        break
      case SendStatus.SUCCESS:
        setTitle(t2(TextId.EYS_SEND_CHANGES_CONFIRMATION_HEADER))
        setDialogText(t2(TextId.EYS_SEND_CHANGES_CONFIRMATION_TEXT))
        break
      case SendStatus.FAILURE:
        setTitle(t2(TextId.ERROR_TRY_LATER))
        setDialogText(t2(TextId.ERROR_REQUEST_FAILED))
        break
      case SendStatus.SENDING:
        setTitle(t2(TextId.HEADER_WAIT))
        setDialogText(t2(TextId.DESCRIPTION_WAIT))
        break
      default:
        setTitle('')
        setDialogText('')
        break
    }
  }, [status, t2])

  const handleCloseClick = useCallback(() => {
    dispatch(closeAllModals())
  }, [dispatch])

  // Eventlistener to close modal on tdsClose (click outside modal and click on close cross)
  useEffect(() => {
    document.addEventListener('tdsClose', handleCloseClick)
    return () => {
      document.removeEventListener('tdsClose', handleCloseClick)
    }
  }, [handleCloseClick])

  return (
    <TdsModal size="md" header={title} show>
      <span slot="body">
        <div>{dialogText}</div>
      </span>

      <span slot="actions" className="tds-u-flex tds-u-gap2 tds-u-flex-wrap">
        {status === SendStatus.WAITING && (
          <TdsButton
            variant="primary"
            size="md"
            text="Confirm"
            onClick={handleSendChanges}
          />
        )}
        <TdsButton
          variant="secondary"
          size="md"
          text={t2(TextId.LABEL_ACTION_CLOSE)}
          onClick={handleCloseClick}
          disabled={status === SendStatus.SENDING}
        />
      </span>
    </TdsModal>
  )
}
